/**
 * DataHealthDashboard.tsx
 *
 * PLAID-69: Data Health Dashboard for VeraScore Admins
 *
 * Displays Plaid item health, staleness alerts, webhook activity,
 * and sandbox audit data from the v_plaid_* views in Supabase.
 * Access is restricted via ProtectedRoute to admin roles.
 */

import { FC, useState, useEffect, useCallback, useMemo } from 'react';
// BUG-DATA-HEALTH-DASHBOARD-EMPTY-PROD-01: Direct supabase-js queries were
// silently filtered to zero rows in production because the browser JWT lacks
// the namespaced admin roles claim that the v_plaid_* RLS policies require.
// All dashboard data is now sourced from the api-verascore-platform backend
// proxy at GET /verascore/admin/data-health, which uses the service role on
// the server and enforces ADMIN-HIDE-002 filtering before responding.
import {
  FiActivity,
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiDatabase,
  FiDownload,
  FiHelpCircle,
  FiPlay,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiTrash2,
  FiXCircle,
} from 'react-icons/fi';
import { SimpleTooltip } from '../components/ui/Tooltip';

// ─── Info Tooltip helper ─────────────────────────────────────────────────────
const InfoTip: FC<{ tip: string }> = ({ tip }) => (
  <SimpleTooltip content={<span className="text-xs leading-relaxed">{tip}</span>} side="right" delayDuration={150}>
    <span className="inline-flex cursor-help"><FiHelpCircle size={14} className="text-slate-400 hover:text-slate-600 transition-colors" /></span>
  </SimpleTooltip>
);

// ─── Types ──────────────────────────────────────────────────────────────────────

interface PlaidItemHealth {
  item_id: string;
  plaid_item_id: string;
  institution_name: string;
  member_uuid: string;
  member_auth0_id: string;
  institution_id: string;
  status: string;
  is_active: boolean;
  staleness_hours: number | null;
  item_health_status: string;
  is_stale: boolean;
  last_successful_update_at: string | null;
  last_sync_started_at: string | null;
  last_sync_completed_at: string | null;
  has_cursor: boolean;
  webhook_url: string | null;
  sync_status: string | null;
  needs_reauth: boolean;
  transactions_enabled: boolean;
  error_code: string | null;
  error_detail: string | null;
  created_at: string;
  updated_at: string;
}

interface StalenessAlert {
  plaid_item_id: string;
  institution_name: string;
  member_auth0_id: string;
  staleness_hours: number | null;
  last_successful_update_at: string | null;
  is_stale: boolean;
}

interface WebhookDailyCount {
  plaid_item_id: string;
  institution_name: string;
  webhook_date: string;
  webhook_types: string[];
  webhook_codes: string[];
  webhook_count: number;
}

interface DeterminismViolation {
  inputs_hash: string;
  algorithm_version: string;
  run_count: number;
  distinct_scores: number;
  scores: number[];
  first_run_ts: string;
  last_run_ts: string;
  member_ids: string[];
  anchor_months: string[];
}

interface SandboxAuditItem {
  plaid_item_id: string;
  institution_name: string;
  member_auth0_id: string;
  status: string;
  created_at: string;
}

// ─── Staleness Event (from plaid_item_staleness_events table) ────────────────

interface StalenessEvent {
  event_id: string;
  plaid_item_id: string;
  item_id: string | null;
  member_id: string | null;
  institution_id: string | null;
  institution_name: string | null;
  detected_at: string;
  hours_stale: number;
  threshold_hours: number;
  severity: string;
  last_successful_update_at: string | null;
  detection_source: string;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_notes: string | null;
  created_at: string;
  // 69E-P1: Operational Ledger columns
  root_cause: string | null;
  resolution_type: string | null;
  recovery_attempts: number | null;
  sla_breach: boolean | null;
  sla_breach_at: string | null;
  freshness_score_at_detection: number | null;
  // 69E-P3: Auto-recovery columns
  recovery_status: string | null;
  next_recovery_at: string | null;
  last_recovery_attempt_at: string | null;
}

// 69E-P4: SLO Metrics (from v_data_freshness_slo view)
interface SloMetrics {
  members_fresh_pct: number;
  total_members_with_items: number;
  members_fresh: number;
  members_stale: number;
  avg_ttr_hours: number | null;
  median_ttr_hours: number | null;
  p95_ttr_hours: number | null;
  active_breaches: number;
  breach_members_affected: number;
  top_failing_institutions: { institution: string; events: number; avg_hours: number }[];
  computed_at: string;
}

// 69E-P1: Root cause badge colors
const ROOT_CAUSE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  WEBHOOK_GAP: { bg: 'bg-orange-100', text: 'text-orange-800', label: '🔧 Webhook Gap' },
  USER_AUTH_FAILURE: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '🔑 Auth Failure' },
  INSTITUTION_ERROR: { bg: 'bg-red-100', text: 'text-red-800', label: '🏦 Institution Error' },
  COLD_ITEM: { bg: 'bg-blue-100', text: 'text-blue-800', label: '❄️ Cold Item' },
  PLAID_OUTAGE: { bg: 'bg-purple-100', text: 'text-purple-800', label: '⚡ Plaid Outage' },
  LOW_RISK_IDLE: { bg: 'bg-slate-100', text: 'text-slate-600', label: '💤 Low Risk' },
  UNKNOWN: { bg: 'bg-slate-100', text: 'text-slate-500', label: '❓ Unknown' },
};

// 69E-P3: Recovery status badge colors
const RECOVERY_STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-800', label: '⏳ Pending' },
  retrying: { bg: 'bg-blue-100', text: 'text-blue-800', label: '🔄 Retrying' },
  recovered: { bg: 'bg-green-100', text: 'text-green-800', label: '✅ Recovered' },
  failed: { bg: 'bg-red-100', text: 'text-red-800', label: '❌ Failed' },
  escalated: { bg: 'bg-red-100', text: 'text-red-800', label: '🚨 Escalated' },
  not_applicable: { bg: 'bg-slate-50', text: 'text-slate-400', label: '— N/A' },
};

// ─── Status Badge Component ─────────────────────────────────────────────────────

const StatusBadge: FC<{ status: string }> = ({ status }) => {
  const colors: Record<string, string> = {
    healthy: 'bg-green-100 text-green-800 border-green-200',
    degraded: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    stale: 'bg-orange-100 text-orange-800 border-orange-200',
    error: 'bg-red-100 text-red-800 border-red-200',
    unknown: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${colors[status] || colors.unknown}`}>
      {status}
    </span>
  );
};

// ─── Severity Badge ─────────────────────────────────────────────────────────────

const SeverityBadge: FC<{ severity: string }> = ({ severity }) => {
  const colors: Record<string, string> = {
    critical: 'bg-red-100 text-red-800',
    warning: 'bg-yellow-100 text-yellow-800',
    info: 'bg-blue-100 text-blue-800',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${colors[severity] || 'bg-slate-100 text-slate-600'}`}>
      {severity}
    </span>
  );
};

// ─── KPI Tile ───────────────────────────────────────────────────────────────────

interface KpiTileProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  status: 'good' | 'warning' | 'critical' | 'neutral';
  subtitle?: string;
}

const KpiTile: FC<KpiTileProps> = ({ title, value, icon, status, subtitle }) => {
  const bg: Record<string, string> = {
    good: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    critical: 'bg-red-50 border-red-200 text-red-800',
    neutral: 'bg-slate-50 border-slate-200 text-slate-800',
  };
  const ic: Record<string, string> = {
    good: 'text-green-600', warning: 'text-yellow-600',
    critical: 'text-red-600', neutral: 'text-slate-600',
  };
  return (
    <div className={`p-4 rounded-lg border-2 ${bg[status]} transition-all hover:shadow-md`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium opacity-75">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs opacity-60 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-white/50 ${ic[status]}`}>{icon}</div>
      </div>
    </div>
  );
};

// ─── Main Dashboard ─────────────────────────────────────────────────────────────

// ─── Sort helper ───────────────────────────────────────────────────────────────

type SortKey = 'institution_name' | 'staleness_hours' | 'status' | 'member_auth0_id';
type SortDir = 'asc' | 'desc';

// ─── Integrations API helper ─────────────────────────────────────────────────

const INTEGRATIONS_API = import.meta.env.VITE_INTEGRATIONS_API_URL || 'https://vs-integrations-development.netlify.app';
const API_BASE = import.meta.env.VITE_API_URL || '/api';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;

/**
 * BUG-DATA-HEALTH-DASHBOARD-EMPTY-PROD-01: Fetch the consolidated data health
 * payload from the backend proxy. The backend uses the Supabase service role
 * to bypass RLS for the v_plaid_* and related views, and applies ADMIN-HIDE-002
 * filtering server-side before responding.
 */
interface DataHealthPayload {
  items: PlaidItemHealth[];
  alerts: StalenessAlert[];
  webhooks: WebhookDailyCount[];
  sandbox: SandboxAuditItem[];
  determinismViolations: DeterminismViolation[];
  stalenessEvents: StalenessEvent[];
  sloMetrics: SloMetrics | null;
  currentAlgoVersion: string | null;
  hiddenMemberCount: number;
  computed_at: string;
}

const fetchDataHealth = async (): Promise<DataHealthPayload> => {
  const response = await fetch(`${API_BASE}/verascore/admin/data-health`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'VS-Client-Id': VS_CLIENT_ID,
    },
    credentials: 'include',
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Data health proxy returned ${response.status}: ${text}`);
  }
  const json = await response.json();
  const payload = json?.data;
  if (!payload) {
    throw new Error('Data health proxy returned an empty payload');
  }
  return payload as DataHealthPayload;
};

/**
 * Fetch the user's Auth0 access_token from api-verascore-platform.
 * The session cookie is sent automatically (same-origin / SameSite=None).
 * We cache the token in sessionStorage to avoid repeated round-trips.
 */
const getSessionToken = async (forceRefresh = false): Promise<string> => {
  if (!forceRefresh) {
    const cached = sessionStorage.getItem('vs_admin_token');
    if (cached) return cached;
  }

  const res = await fetch(`${API_BASE}/verascore/session-token`, {
    method: 'GET',
    headers: { 'VS-Client-Id': VS_CLIENT_ID, 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Failed to retrieve session token (${res.status})`);
  const json = await res.json();
  const token = json?.data?.access_token;
  if (!token) throw new Error('No access_token in session-token response');
  sessionStorage.setItem('vs_admin_token', token);
  return token;
};

const callAdminApi = async (path: string, body: Record<string, unknown>) => {
  // 1. Get the access token from api-verascore-platform (cookie-based, same domain)
  let token = await getSessionToken();

  // 2. Call vs-integrations with Bearer auth (cross-origin, no cookies)
  const res = await fetch(`${INTEGRATIONS_API}/plaid/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'VS-Client-Id': VS_CLIENT_ID,
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // If 401/403, clear cached token, get a fresh one, and retry once
    if (res.status === 401 || res.status === 403) {
      sessionStorage.removeItem('vs_admin_token');
      token = await getSessionToken(true);
      const retry = await fetch(`${INTEGRATIONS_API}/plaid/${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'VS-Client-Id': VS_CLIENT_ID,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!retry.ok) {
        const text = await retry.text();
        throw new Error(`${retry.status}: ${text}`);
      }
      return retry.json();
    }
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json();
};

// ─── Main Dashboard ─────────────────────────────────────────────────────────

const DataHealthDashboard: FC = () => {
  const [items, setItems] = useState<PlaidItemHealth[]>([]);
  const [alerts, setAlerts] = useState<StalenessAlert[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookDailyCount[]>([]);
  const [sandbox, setSandbox] = useState<SandboxAuditItem[]>([]);
  const [determinismViolations, setDeterminismViolations] = useState<DeterminismViolation[]>([]);
  const [currentAlgoVersion, setCurrentAlgoVersion] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<string>('');

  // A: Search
  const [search, setSearch] = useState('');

  // D: Sort
  const [sortKey, setSortKey] = useState<SortKey>('staleness_hours');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // E: Expanded row
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // B: Action state
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<{ id: string; ok: boolean; msg: string } | null>(null);

  // 69-E Phase 0: Staleness Event Log
  const [stalenessEvents, setStalenessEvents] = useState<StalenessEvent[]>([]);
  const [eventFilter, setEventFilter] = useState<'all' | 'open' | 'resolved'>('all');

  // 69-E Phase 0: Operations Console
  const [opsLoading, setOpsLoading] = useState<string | null>(null);
  const [opsResult, setOpsResult] = useState<{ action: string; data: Record<string, unknown> } | null>(null);
  const [alarmCooldownUntil, setAlarmCooldownUntil] = useState<number>(0);
  const [sweeperCooldownUntil, setSweeperCooldownUntil] = useState<number>(0);

  // 69-E Phase 4: SLO Metrics
  const [sloMetrics, setSloMetrics] = useState<SloMetrics | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // BUG-DATA-HEALTH-DASHBOARD-EMPTY-PROD-01: single backend round-trip.
      // The server-side proxy handles RLS (service role) and ADMIN-HIDE-002.
      const payload = await fetchDataHealth();

      setItems(payload.items || []);
      setAlerts(payload.alerts || []);
      setWebhooks(payload.webhooks || []);
      setSandbox(payload.sandbox || []);
      setDeterminismViolations(payload.determinismViolations || []);
      setStalenessEvents(payload.stalenessEvents || []);
      setCurrentAlgoVersion(payload.currentAlgoVersion ?? null);
      setSloMetrics(payload.sloMetrics ?? null);

      setLastRefresh(new Date().toLocaleTimeString());
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Computed KPIs
  const totalItems = items.length;
  const healthyCount = items.filter(i => !i.is_stale && !i.error_code).length;
  const staleCount = alerts.length;
  const errorCount = items.filter(i => i.error_code).length;
  const sandboxCount = sandbox.length;

  const formatHours = (h: number | null) => {
    if (h === null) return 'N/A';
    if (h < 1) return `${Math.round(h * 60)}m`;
    if (h < 48) return `${Math.round(h)}h`;
    return `${Math.round(h / 24)}d`;
  };

  // A: Search filter
  const q = search.toLowerCase().trim();
  const matchesSearch = (row: { institution_name?: string; member_auth0_id?: string }) =>
    !q || (row.institution_name || '').toLowerCase().includes(q) || (row.member_auth0_id || '').toLowerCase().includes(q);

  const filteredItems = useMemo(() => {
    const filtered = items.filter(matchesSearch);
    return [...filtered].sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, q, sortKey, sortDir]);

  const filteredAlerts = useMemo(() => alerts.filter(matchesSearch), [alerts, q]);
  const filteredWebhooks = useMemo(() => webhooks.filter(matchesSearch), [webhooks, q]);
  const filteredSandbox = useMemo(() => sandbox.filter(matchesSearch), [sandbox, q]);

  // Determinism: classify violations as active (current version) vs retired
  const activeViolations = useMemo(
    () => determinismViolations.filter(v => v.algorithm_version === currentAlgoVersion),
    [determinismViolations, currentAlgoVersion]
  );
  const retiredViolations = useMemo(
    () => determinismViolations.filter(v => v.algorithm_version !== currentAlgoVersion),
    [determinismViolations, currentAlgoVersion]
  );

  // D: Sort toggle
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortKey(key); setSortDir('asc'); }
  };
  const SortIcon: FC<{ col: SortKey }> = ({ col }) => {
    if (sortKey !== col) return <FiChevronDown size={12} className="inline ml-0.5 opacity-30" />;
    return sortDir === 'asc'
      ? <FiChevronUp size={12} className="inline ml-0.5 text-blue-600" />
      : <FiChevronDown size={12} className="inline ml-0.5 text-blue-600" />;
  };

  // B: Action handlers
  const handleTriggerSync = async (itemId: string, institutionName?: string) => {
    const label = institutionName || 'this item';
    if (!confirm(`Refresh data from ${label}?\n\nThis will pull the latest transactions and balances from the institution.`)) return;
    setActionLoading(itemId);
    setActionResult(null);
    try {
      await callAdminApi('trigger-sync', { item_id: itemId, skip_balances: false });
      setActionResult({ id: itemId, ok: true, msg: `${label} — sync started successfully` });
      fetchData();
    } catch (err: any) {
      setActionResult({ id: itemId, ok: false, msg: `${label} — sync failed: ${err.message}` });
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchive = async (itemId: string, name: string) => {
    if (!confirm(`Archive "${name}"?\n\nThis will permanently deactivate this Plaid connection and remove the access token. This cannot be undone.`)) return;
    setActionLoading(itemId);
    setActionResult(null);
    try {
      await callAdminApi('archive-item', { item_id: itemId, reason: 'sandbox_cleanup' });
      setActionResult({ id: itemId, ok: true, msg: `${name} — archived successfully` });
      fetchData();
    } catch (err: any) {
      setActionResult({ id: itemId, ok: false, msg: `${name} — archive failed: ${err.message}` });
    } finally {
      setActionLoading(null);
    }
  };

  // 69-E Phase 0: Operations Console — invoke scheduled functions directly
  const COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes

  const handleRunFunction = async (fnName: 'staleness-alarm' | 'psm-sweeper') => {
    const label = fnName === 'staleness-alarm' ? 'Staleness Alarm' : 'PSM Sweeper';
    const cooldownUntil = fnName === 'staleness-alarm' ? alarmCooldownUntil : sweeperCooldownUntil;
    const setCooldown = fnName === 'staleness-alarm' ? setAlarmCooldownUntil : setSweeperCooldownUntil;

    if (Date.now() < cooldownUntil) {
      const secs = Math.ceil((cooldownUntil - Date.now()) / 1000);
      alert(`Please wait ${secs}s before running ${label} again.`);
      return;
    }
    if (!confirm(`Run ${label} now? This will check all Plaid items for staleness.`)) return;

    setOpsLoading(fnName);
    setOpsResult(null);
    try {
      const token = await getSessionToken();
      const res = await fetch(`${INTEGRATIONS_API}/.netlify/functions/${fnName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'VS-Client-Id': VS_CLIENT_ID,
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) sessionStorage.removeItem('vs_admin_token');
        const text = await res.text();
        throw new Error(`${res.status}: ${text}`);
      }
      const data = await res.json();
      setOpsResult({ action: label, data });
      setCooldown(Date.now() + COOLDOWN_MS);
      fetchData(); // auto-refresh
    } catch (err: any) {
      setOpsResult({ action: label, data: { error: err.message } });
    } finally {
      setOpsLoading(null);
    }
  };

  // 69-E Phase 0: Audit Export — JSON download
  const handleExportEvents = () => {
    const filtered = filteredEvents;
    const blob = new Blob([JSON.stringify({ exported_at: new Date().toISOString(), count: filtered.length, events: filtered }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `staleness-events-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // 69-E Phase 0: Event log filtering
  const filteredEvents = useMemo(() => {
    if (eventFilter === 'open') return stalenessEvents.filter(e => !e.resolved_at);
    if (eventFilter === 'resolved') return stalenessEvents.filter(e => !!e.resolved_at);
    return stalenessEvents;
  }, [stalenessEvents, eventFilter]);

  // 69-E Phase 0: Relative time formatter
  const relativeTime = (iso: string | null) => {
    if (!iso) return '—';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 48) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading Data Health...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FiDatabase className="text-brand-blue" /> Data Health Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">PLAID-69 • Plaid Integration Monitor</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <FiRefreshCw size={14} /> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <FiXCircle className="inline mr-1" /> {error}
        </div>
      )}

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiTile title="Total Items" value={totalItems} icon={<FiDatabase size={20} />} status="neutral" />
        <KpiTile title="Healthy" value={healthyCount} icon={<FiCheckCircle size={20} />}
          status={healthyCount === totalItems ? 'good' : 'warning'}
          subtitle={`${totalItems > 0 ? Math.round((healthyCount / totalItems) * 100) : 0}%`} />
        <KpiTile title="Stale Alerts" value={staleCount} icon={<FiAlertTriangle size={20} />}
          status={staleCount === 0 ? 'good' : staleCount >= 3 ? 'critical' : 'warning'} />
        <KpiTile title="Errors" value={errorCount} icon={<FiXCircle size={20} />}
          status={errorCount === 0 ? 'good' : 'critical'} />
      </div>

      {/* Scoring Determinism Guard — WP#83 (KPI + detail combined) */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <FiShield className="text-emerald-500" /> Scoring Determinism Guard <InfoTip tip="Verifies that the same inputs always produce the same score. A PASS means the scoring engine is fully reproducible — critical for audit and regulatory compliance." />
          <span className={`ml-2 px-2 py-0.5 text-xs font-semibold rounded-full ${
            activeViolations.length > 0
              ? 'bg-red-100 text-red-800'
              : 'bg-green-100 text-green-800'
          }`}>
            {activeViolations.length > 0
              ? `${activeViolations.length} ACTIVE VIOLATION${activeViolations.length > 1 ? 'S' : ''}`
              : 'PASS'}
          </span>
          {retiredViolations.length > 0 && activeViolations.length === 0 && (
            <span className="ml-1 px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-500">
              {retiredViolations.length} retired
            </span>
          )}
        </h2>

        {/* Active version status */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-green-800">
            {activeViolations.length === 0
              ? <>✅ No determinism violations in current production version <code className="bg-green-100 px-1 rounded text-xs font-mono">{currentAlgoVersion || '—'}</code>. The scoring engine is fully deterministic.</>
              : <>🔴 <strong>{activeViolations.length} violation{activeViolations.length > 1 ? 's' : ''}</strong> detected in current production version <code className="bg-red-100 px-1 rounded text-xs font-mono">{currentAlgoVersion}</code>. <strong>Investigate immediately.</strong></>
            }
          </p>
          <p className="text-xs text-green-600 mt-1">
            {retiredViolations.length > 0
              ? `${retiredViolations.length} historical violation${retiredViolations.length > 1 ? 's' : ''} in retired versions • `
              : ''}
            Current: {currentAlgoVersion || '—'} • Same inputs + same version → same score
          </p>
        </div>

        {/* Active violations table (red) — only if any exist */}
        {activeViolations.length > 0 && (
          <div className="bg-white border-2 border-red-300 rounded-lg overflow-hidden mb-4">
            <div className="bg-red-50 border-b border-red-200 px-4 py-2">
              <p className="text-sm font-semibold text-red-800">
                🔴 Active Version Violations — {currentAlgoVersion}
              </p>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-red-50/50 border-b border-red-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-red-700">inputs_hash</th>
                  <th className="px-4 py-3 text-left font-medium text-red-700">Version</th>
                  <th className="px-4 py-3 text-left font-medium text-red-700">Runs</th>
                  <th className="px-4 py-3 text-left font-medium text-red-700">Scores</th>
                  <th className="px-4 py-3 text-left font-medium text-red-700">Anchor</th>
                  <th className="px-4 py-3 text-left font-medium text-red-700">First Run</th>
                  <th className="px-4 py-3 text-left font-medium text-red-700">Last Run</th>
                </tr>
              </thead>
              <tbody>
                {activeViolations.map((v) => (
                  <tr key={`${v.inputs_hash}-${v.algorithm_version}`} className="border-b border-red-100 hover:bg-red-50">
                    <td className="px-4 py-3 font-mono text-xs">{v.inputs_hash.slice(0, 12)}…</td>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-red-700">{v.algorithm_version}</td>
                    <td className="px-4 py-3">{v.run_count}</td>
                    <td className="px-4 py-3">
                      <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                        [{v.scores?.join(', ')}]
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">{v.anchor_months?.join(', ')}</td>
                    <td className="px-4 py-3 text-xs">{v.first_run_ts ? new Date(v.first_run_ts).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3 text-xs">{v.last_run_ts ? new Date(v.last_run_ts).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Retired violations table (muted slate/amber) — always shown if any exist */}
        {retiredViolations.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden opacity-75">
            <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center gap-2">
              <p className="text-sm font-medium text-slate-500">
                📁 Retired Version Violations ({retiredViolations.length})
              </p>
              <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-slate-200 text-slate-500 uppercase tracking-wide">
                historical — no action needed
              </span>
            </div>
            <table className="w-full text-sm text-slate-500">
              <thead className="bg-slate-50/50 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-slate-400 text-xs">inputs_hash</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-400 text-xs">Version</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-400 text-xs">Runs</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-400 text-xs">Scores</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-400 text-xs">Anchor</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-400 text-xs">First Run</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-400 text-xs">Last Run</th>
                </tr>
              </thead>
              <tbody>
                {retiredViolations.map((v) => (
                  <tr key={`${v.inputs_hash}-${v.algorithm_version}`} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs text-slate-400">{v.inputs_hash.slice(0, 12)}…</td>
                    <td className="px-4 py-2 font-mono text-xs">
                      <span className="bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded text-[10px]">{v.algorithm_version}</span>
                    </td>
                    <td className="px-4 py-2 text-xs">{v.run_count}</td>
                    <td className="px-4 py-2">
                      <span className="bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full text-xs">
                        [{v.scores?.join(', ')}]
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs">{v.anchor_months?.join(', ')}</td>
                    <td className="px-4 py-2 text-xs">{v.first_run_ts ? new Date(v.first_run_ts).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-2 text-xs">{v.last_run_ts ? new Date(v.last_run_ts).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* A: Search Bar */}
      <div className="mb-6 relative">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by member ID or institution name…"
          className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <FiXCircle size={16} />
          </button>
        )}
      </div>

      {/* Action toast */}
      {actionResult && (
        <div className={`mb-4 p-3 rounded-lg text-sm flex items-center justify-between ${actionResult.ok ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          <span>{actionResult.ok ? '✓' : '✗'} {actionResult.msg}</span>
          <button onClick={() => setActionResult(null)} className="ml-2 opacity-60 hover:opacity-100">✕</button>
        </div>
      )}

      {/* Staleness Alerts */}
      {filteredAlerts.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <FiAlertTriangle className="text-orange-500" /> Staleness Alerts <InfoTip tip="Plaid connections that haven't received fresh data in over 48 hours. Click Sync to manually pull the latest transactions and balances from the institution." />
            {q && <span className="text-xs font-normal text-slate-400">({filteredAlerts.length} of {alerts.length})</span>}
          </h2>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Institution</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Member</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Staleness</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Severity</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Last Update</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredAlerts.map((a) => (
                  <tr key={a.plaid_item_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">{a.institution_name}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs font-mono">{a.member_auth0_id}</td>
                    <td className="px-4 py-3">{formatHours(a.staleness_hours)}</td>
                    <td className="px-4 py-3"><SeverityBadge severity={a.staleness_hours === null ? 'critical' : a.staleness_hours > 96 ? 'critical' : 'warning'} /></td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {a.last_successful_update_at ? new Date(a.last_successful_update_at).toLocaleString() : 'Never'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleTriggerSync(a.plaid_item_id, a.institution_name)}
                        disabled={actionLoading === a.plaid_item_id}
                        className="px-2.5 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        {actionLoading === a.plaid_item_id ? '…' : '⟳ Sync'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 69-E Phase 0: Operations Console */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <FiPlay className="text-purple-500" /> Operations Console <InfoTip tip="Manually run staleness detection and remediation functions. The Staleness Alarm scans all items for stale data (runs automatically every 6 hours). The PSM Sweeper detects and remediates stuck connections (runs every 15 minutes)." />
        </h2>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <p className="text-xs text-slate-500 mb-3">Manually invoke staleness detection functions. Results appear inline and the dashboard auto-refreshes.</p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleRunFunction('staleness-alarm')}
              disabled={!!opsLoading || Date.now() < alarmCooldownUntil}
              className="px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {opsLoading === 'staleness-alarm' ? (
                <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Running…</>
              ) : (
                <>🔔 Run Staleness Alarm</>
              )}
            </button>
            <button
              onClick={() => handleRunFunction('psm-sweeper')}
              disabled={!!opsLoading || Date.now() < sweeperCooldownUntil}
              className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {opsLoading === 'psm-sweeper' ? (
                <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Running…</>
              ) : (
                <>⚙️ Run PSM Sweeper</>
              )}
            </button>
            {(Date.now() < alarmCooldownUntil || Date.now() < sweeperCooldownUntil) && (
              <span className="text-xs text-slate-400 self-center">Cooldown active (5 min between invocations)</span>
            )}
          </div>
          {opsResult && (
            <div className={`mt-3 p-3 rounded-lg text-sm ${opsResult.data.error ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">{opsResult.data.error ? '✗' : '✓'} {opsResult.action}</span>
                <button onClick={() => setOpsResult(null)} className="opacity-60 hover:opacity-100">✕</button>
              </div>
              <pre className="mt-1 text-xs font-mono whitespace-pre-wrap">{JSON.stringify(opsResult.data, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>

      {/* 69-E Phase 4: SLO Compliance Panel */}
      {sloMetrics && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <FiActivity className="text-emerald-500" /> SLO Compliance <InfoTip tip="Service Level Objective tracking over a 30-day window. Our SLO target is ≥99% of members with all data fresh (under 72 hours old). Breaches indicate members whose data exceeds the freshness threshold." />
            <span className="text-xs font-normal text-slate-400">(30-day window)</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {/* Members Fresh % */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">Members Fresh <InfoTip tip="Percentage of members whose Plaid connections all have data less than 72 hours old. Target: ≥99%." /></div>
              <div className={`text-2xl font-bold ${Number(sloMetrics.members_fresh_pct) >= 99 ? 'text-green-600' : Number(sloMetrics.members_fresh_pct) >= 90 ? 'text-yellow-600' : 'text-red-600'}`}>
                {Number(sloMetrics.members_fresh_pct).toFixed(1)}%
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {sloMetrics.members_fresh} of {sloMetrics.total_members_with_items} members
              </div>
              <div className={`text-xs mt-1 font-medium ${Number(sloMetrics.members_fresh_pct) >= 99 ? 'text-green-600' : 'text-red-600'}`}>
                {Number(sloMetrics.members_fresh_pct) >= 99 ? '✓ SLO Met (≥99%)' : '⚠ SLO Breach (<99%)'}
              </div>
            </div>

            {/* Active Breaches */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">Active SLA Breaches <InfoTip tip="Number of staleness events where data has been stale for over 72 hours without resolution. Each breach means a member's score may be based on outdated data." /></div>
              <div className={`text-2xl font-bold ${sloMetrics.active_breaches === 0 ? 'text-green-600' : 'text-red-600'}`}>
                {sloMetrics.active_breaches}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {sloMetrics.breach_members_affected} member{sloMetrics.breach_members_affected !== 1 ? 's' : ''} affected
              </div>
            </div>

            {/* Avg TTR */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">Avg Time-to-Recovery <InfoTip tip="Average time from when staleness is detected to when fresh data is restored. P50 is the median, P95 is the worst 5%. Lower is better." /></div>
              <div className="text-2xl font-bold text-slate-800">
                {sloMetrics.avg_ttr_hours != null ? `${Number(sloMetrics.avg_ttr_hours).toFixed(1)}h` : '—'}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                P50: {sloMetrics.median_ttr_hours != null ? `${Number(sloMetrics.median_ttr_hours).toFixed(1)}h` : '—'}
                {' · '}
                P95: {sloMetrics.p95_ttr_hours != null ? `${Number(sloMetrics.p95_ttr_hours).toFixed(1)}h` : '—'}
              </div>
              <div className={`text-xs mt-1 font-medium ${sloMetrics.avg_ttr_hours != null && Number(sloMetrics.avg_ttr_hours) <= 6 ? 'text-green-600' : sloMetrics.avg_ttr_hours != null ? 'text-red-600' : 'text-slate-400'}`}>
                {sloMetrics.avg_ttr_hours != null ? (Number(sloMetrics.avg_ttr_hours) <= 6 ? '✓ Within SLO (<6h)' : '⚠ SLO Breach (>6h)') : 'No data yet'}
              </div>
            </div>

            {/* Impact Radius */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">Impact Radius <InfoTip tip="Number of members currently affected by stale data. Their scores may not reflect their latest financial activity." /></div>
              <div className="text-2xl font-bold text-slate-800">
                {sloMetrics.members_stale}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                member{sloMetrics.members_stale !== 1 ? 's' : ''} with stale data
              </div>
              <div className="text-xs text-slate-400">
                Scores may be impacted
              </div>
            </div>
          </div>

          {/* Top Failing Institutions */}
          {Array.isArray(sloMetrics.top_failing_institutions) && sloMetrics.top_failing_institutions.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">Top Failing Institutions (30 days) <InfoTip tip="Institutions with the most staleness events in the last 30 days. Helps identify systemic issues with specific banks or data providers." /></div>
              <div className="flex flex-wrap gap-3">
                {sloMetrics.top_failing_institutions.map((inst, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-full text-xs">
                    <span className="font-medium text-red-800">{inst.institution}</span>
                    <span className="text-red-600">{inst.events} events</span>
                    <span className="text-red-400">avg {inst.avg_hours}h</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 69-E Phase 0: Staleness Event Log */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <FiClock className="text-teal-500" /> Staleness Event Log <InfoTip tip="Chronological record of every staleness event — when stale data was detected, the root cause, severity, and whether it was resolved. Use filters to view open or resolved events. Export as JSON for audit evidence." />
          <span className="text-xs font-normal text-slate-400">({filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''})</span>
        </h2>
        <div className="flex items-center gap-2 mb-3">
          {(['all', 'open', 'resolved'] as const).map(f => (
            <button
              key={f}
              onClick={() => setEventFilter(f)}
              className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${eventFilter === f ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
            >
              {f === 'all' ? 'All' : f === 'open' ? '🔴 Open' : '🟢 Resolved'}
            </button>
          ))}
          <button
            onClick={handleExportEvents}
            disabled={filteredEvents.length === 0}
            className="ml-auto px-3 py-1 text-xs font-medium text-slate-600 border border-slate-300 rounded-full hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1 transition-colors"
          >
            <FiDownload size={12} /> Export JSON
          </button>
        </div>
        {filteredEvents.length === 0 ? (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500">
            No staleness events {eventFilter !== 'all' ? `(${eventFilter})` : ''} recorded yet.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Institution</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Severity</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Root Cause</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Hours Stale</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Source</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">SLA</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Recovery</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Detected</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Resolved</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((ev) => {
                  const rcStyle = ROOT_CAUSE_COLORS[ev.root_cause || 'UNKNOWN'] || ROOT_CAUSE_COLORS.UNKNOWN;
                  return (
                  <tr key={ev.event_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">{ev.institution_name || '—'}</td>
                    <td className="px-4 py-3"><SeverityBadge severity={ev.severity} /></td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${rcStyle.bg} ${rcStyle.text}`}>{rcStyle.label}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {Math.round(ev.hours_stale)}h
                      {ev.freshness_score_at_detection != null && (
                        <span className="ml-1 text-slate-400" title="Freshness score at detection">({ev.freshness_score_at_detection})</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600">{ev.detection_source}</span>
                    </td>
                    <td className="px-4 py-3">
                      {ev.sla_breach
                        ? <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800" title={ev.sla_breach_at ? `Breached at ${ev.sla_breach_at}` : ''}>⚠️ BREACH</span>
                        : <span className="text-xs text-slate-400">OK</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const rs = RECOVERY_STATUS_COLORS[ev.recovery_status || 'not_applicable'] || RECOVERY_STATUS_COLORS.not_applicable;
                        const attempts = ev.recovery_attempts ?? 0;
                        const title = ev.next_recovery_at ? `Next retry: ${new Date(ev.next_recovery_at).toLocaleString()}` : ev.last_recovery_attempt_at ? `Last attempt: ${new Date(ev.last_recovery_attempt_at).toLocaleString()}` : '';
                        return (
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${rs.bg} ${rs.text}`} title={title}>
                            {rs.label}{attempts > 0 ? ` (${attempts})` : ''}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500" title={ev.detected_at}>{relativeTime(ev.detected_at)}</td>
                    <td className="px-4 py-3 text-xs text-slate-500" title={ev.resolved_at || ''}>{relativeTime(ev.resolved_at)}</td>
                    <td className="px-4 py-3">
                      {ev.resolved_at
                        ? <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">🟢 Resolved</span>
                        : <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">🔴 Open</span>
                      }
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Item Health Table — sortable + expandable */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <FiShield className="text-blue-500" /> Plaid Item Health <InfoTip tip="Live status of every Plaid connection. Shows sync freshness, errors, and connection health. Click any row to expand details. Green checkmark = healthy, red = error or stale." />
          {q && <span className="text-xs font-normal text-slate-400">({filteredItems.length} of {items.length})</span>}
        </h2>
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('institution_name')}>
                  Institution <SortIcon col="institution_name" />
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('member_auth0_id')}>
                  Member <SortIcon col="member_auth0_id" />
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('status')}>
                  Status <SortIcon col="status" />
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Health</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('staleness_hours')}>
                  Staleness <SortIcon col="staleness_hours" />
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Error</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Cursor</th>
                <th className="px-4 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <>
                  <tr
                    key={item.plaid_item_id}
                    className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${expandedId === item.plaid_item_id ? 'bg-blue-50' : ''}`}
                    onClick={() => setExpandedId(expandedId === item.plaid_item_id ? null : item.plaid_item_id)}
                  >
                    <td className="px-4 py-3 font-medium">{item.institution_name}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs font-mono">{item.member_auth0_id}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={item.item_health_status} /></td>
                    <td className="px-4 py-3">{formatHours(item.staleness_hours)}</td>
                    <td className="px-4 py-3 text-xs text-red-600">{item.error_code || '—'}</td>
                    <td className="px-4 py-3">
                      {item.has_cursor ? <FiCheckCircle className="text-green-500" size={14} /> : <FiXCircle className="text-slate-300" size={14} />}
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {expandedId === item.plaid_item_id ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                    </td>
                  </tr>
                  {/* E: Expanded detail row */}
                  {expandedId === item.plaid_item_id && (
                    <tr key={`${item.plaid_item_id}-detail`} className="bg-slate-50 border-b border-slate-200">
                      <td colSpan={8} className="px-6 py-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                          <div><span className="text-slate-400">Plaid Item ID</span><br /><span className="font-mono">{item.plaid_item_id}</span></div>
                          <div><span className="text-slate-400">Internal Item ID</span><br /><span className="font-mono">{item.item_id}</span></div>
                          <div><span className="text-slate-400">Institution ID</span><br /><span className="font-mono">{item.institution_id}</span></div>
                          <div><span className="text-slate-400">Member UUID</span><br /><span className="font-mono">{item.member_uuid}</span></div>
                          <div><span className="text-slate-400">Last Sync Started</span><br />{item.last_sync_started_at ? new Date(item.last_sync_started_at).toLocaleString() : '—'}</div>
                          <div><span className="text-slate-400">Last Sync Completed</span><br />{item.last_sync_completed_at ? new Date(item.last_sync_completed_at).toLocaleString() : '—'}</div>
                          <div><span className="text-slate-400">Last Successful Update</span><br />{item.last_successful_update_at ? new Date(item.last_successful_update_at).toLocaleString() : 'Never'}</div>
                          <div><span className="text-slate-400">Created</span><br />{new Date(item.created_at).toLocaleString()}</div>
                          <div><span className="text-slate-400">Webhook URL</span><br /><span className="font-mono break-all">{item.webhook_url || '—'}</span></div>
                          <div><span className="text-slate-400">Sync Status</span><br />{item.sync_status || '—'}</div>
                          <div><span className="text-slate-400">Needs Reauth</span><br />{item.needs_reauth ? '⚠️ Yes' : 'No'}</div>
                          <div><span className="text-slate-400">Transactions Enabled</span><br />{item.transactions_enabled ? 'Yes' : 'No'}</div>
                          {item.error_detail && <div className="col-span-2"><span className="text-slate-400">Error Detail</span><br /><span className="text-red-600">{item.error_detail}</span></div>}
                        </div>
                        <div className="mt-3 flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleTriggerSync(item.item_id, item.institution_name); }}
                            disabled={actionLoading === item.item_id}
                            className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {actionLoading === item.item_id ? 'Syncing…' : '⟳ Trigger Sync'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Webhook Activity — C: Institution column added */}
      {filteredWebhooks.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <FiActivity className="text-indigo-500" /> Webhook Activity (Last 30 Days) <InfoTip tip="Plaid webhook events received in the last 30 days. Shows which institutions are actively sending data updates (transactions, liabilities). A healthy connection sends regular DEFAULT_UPDATE and SYNC_UPDATES_AVAILABLE webhooks." />
            {q && <span className="text-xs font-normal text-slate-400">({filteredWebhooks.length} of {webhooks.length})</span>}
          </h2>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Date</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Institution</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Code</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-600">Count</th>
                </tr>
              </thead>
              <tbody>
                {filteredWebhooks.map((w, i) => (
                  <tr key={`${w.webhook_date}-${w.plaid_item_id}-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">{w.webhook_date}</td>
                    <td className="px-4 py-3 font-medium">{w.institution_name || '—'}</td>
                    <td className="px-4 py-3">{(w.webhook_types || []).join(', ')}</td>
                    <td className="px-4 py-3 text-slate-600">{(w.webhook_codes || []).join(', ')}</td>
                    <td className="px-4 py-3 text-right font-mono">{w.webhook_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sandbox Audit — B: Archive button */}
      {filteredSandbox.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <FiClock className="text-amber-500" /> Sandbox Items (#70 Audit) <InfoTip tip="Plaid items connected using sandbox/test credentials detected in this environment. These should be archived to keep production data clean. Use the Archive button to decommission." />
          </h2>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800 mb-3">
              {filteredSandbox.length} sandbox item(s) detected in production. Use the Archive button to decommission.
            </p>
            <div className="space-y-2">
              {filteredSandbox.map((s) => (
                <div key={s.plaid_item_id} className="flex items-center justify-between bg-white rounded px-3 py-2 border border-amber-100">
                  <div>
                    <span className="font-medium text-sm">{s.institution_name}</span>
                    <span className="text-xs text-slate-500 ml-2">{s.member_auth0_id}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400 font-mono">{s.plaid_item_id.substring(0, 12)}…</span>
                    <button
                      onClick={() => handleArchive(s.plaid_item_id, s.institution_name)}
                      disabled={actionLoading === s.plaid_item_id}
                      className="px-2.5 py-1 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1 transition-colors"
                    >
                      <FiTrash2 size={11} /> {actionLoading === s.plaid_item_id ? '…' : 'Archive'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-center text-xs text-slate-400 py-4">
        Last refreshed: {lastRefresh || '—'} • Data from v_plaid_* views
      </div>
    </div>
  );
};

export default DataHealthDashboard;
